import React, { useEffect } from "react";
import { Range } from "rc-slider";
import IDataFilterRangeCurrencyValue from "./IDataFilterRangeCurrencyValue";
import styles from "./datafilterrange.module.scss";
import "rc-slider/assets/index.css";
import AmountLimitDTO from "features/shared/dtos/AmountLimitDTO";
import IDataFilterSelectInputValue from "../selectBox/IDataFilterSelectInputValue";
import { convertCodeToLabel } from "features/shared/helpers/currencyHelper";
import FormGroup from "features/shared/forms/components/FormGroup";
import IDataFilterProps from "../IDataFilterProps";
import { getTranslatedFormText } from "features/shared/helpers/translationHelper";
import { useTranslation } from "react-i18next";

const DataFilterRangeBoxCurrency = (props: IDataFilterProps) => {
  // translations
  const tLables = useTranslation("shared\\dataFilters").t;
  const tComponent = useTranslation("shared\\components").t;
  
  const rangeValue = props.filterDescriptor
    .value as IDataFilterRangeCurrencyValue;

  const currencySelectorDescriptor = props.descriptors.find(
    (x) => x.name === rangeValue.currencySelector
  );

  const currencySelectorValue =
    currencySelectorDescriptor?.value as IDataFilterSelectInputValue;

  const setDescriptorValue = (name: string, value: number[]) => {
    const newDescriptors = [...props.descriptors];

    // Získám descriptor
    const descriptor = newDescriptors.find((x) => x.name === name);

    // Nastavím hodnotu
    const range = descriptor?.value as IDataFilterRangeCurrencyValue;
    range.value = value;

    props.setDescriptors(newDescriptors);
  };

  const setDescriptorValueBoundary = (
    name: string,
    min: number,
    max: number
  ) => {
    const newDescriptors = [...props.descriptors];

    // Získám descriptor
    const descriptor = newDescriptors.find((x) => x.name === name);

    // Nastavím hodnotu
    const currencyValue = descriptor?.value as IDataFilterRangeCurrencyValue;
    currencyValue.min = min;
    currencyValue.max = max;

    props.setDescriptors(newDescriptors);
  };

  const handleOnChangeRangeBox = (value: number[], name: string) => {
    if (value[0] === 0 && value[1] === 0) return;

    setDescriptorValue(name, value);
  };

  const setExternalData = () => {
    if (
      rangeValue.external &&
      props.externalData &&
      props.externalData.length > 0
    ) {
      // Externí data pro Range
      const externalData = props.externalData.find(
        (x) => x.name === props.filterDescriptor.name
      );
      if (!externalData) return;

      const amountLimit = externalData.data as AmountLimitDTO[];

      // Descriptor výběru měny

      if (!currencySelectorDescriptor) return;

      // const currencySelectorValue = currencySelectorDescriptor.value as IDataFilterSelectInputValue;
      if (!currencySelectorValue) return;

      // Pokud se soucasny a vybrany kod shoduji nenastavuju
      if (currencySelectorValue.value === "") {
        resetRange();
        return;
      }

      // Vyhledám v externích datech hodnotu podle zvoleného descriptoru měny
      const selectedCurrencyValue = amountLimit.find(
        (x) => x.currencyCode === currencySelectorValue.value
      );

      // Nastavím hodnoty
      if (selectedCurrencyValue) {
        setDescriptorValueBoundary(
          props.filterDescriptor.name,
          selectedCurrencyValue?.min,
          selectedCurrencyValue?.max
        );

        let setMin = selectedCurrencyValue.min;
        let setMax = selectedCurrencyValue.max;

        if (rangeValue.value && rangeValue.value[0] !== undefined) {
          setMin = rangeValue.value[0];
        }

        if (rangeValue.value && rangeValue.value[1] !== undefined) {
          setMax = rangeValue.value[1];
        }

        setDescriptorValue(props.filterDescriptor.name, [setMin, setMax]);
      } else {
        resetRange();
      }
    }
  };

  const resetRange = () => {
    setDescriptorValueBoundary(props.filterDescriptor.name, 0, 0);
    setDescriptorValue(props.filterDescriptor.name, []);
  };

  const handleOnChangeValueInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    currentRange: number[],
    descriptorName: string
  ) => {
    let newValues = currentRange;

    let valueNumber = Number.parseFloat(e.target.value.replace(",", "."));
    valueNumber = !isNaN(valueNumber) ? valueNumber : 0;

    if (e.target.id === "rangeValueFrom") {
      newValues[0] = valueNumber;
    } else {
      newValues[1] = valueNumber;
    }

    setDescriptorValue(descriptorName, newValues);
  };

  const handleOnBlurValueInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    currentRange: number[],
    descriptorName: string
  ) => {
    // Kontrola min / max
    const newValues = switchRange(currentRange);
    setDescriptorValue(descriptorName, newValues);
  };

  // Pokud je hodnota min větší než max, tak ji přehodím
  const switchRange = (currentRange: number[]): number[] => {
    if (currentRange[0] > currentRange[1]) {
      return [currentRange[1], currentRange[0]];
    }

    return currentRange;
  };

  useEffect(() => {
    setExternalData();
  }, [currencySelectorValue.value]);

  const disabled = currencySelectorValue.value === "";
  return (
    <FormGroup
      key={props.reactKey}
      labelText={`${getTranslatedFormText(props.filterDescriptor, "label", tLables)} [${
        convertCodeToLabel(currencySelectorValue.value) || tComponent("dataFilterRangeBoxCurrency.selectCurrency")
      }]`}
    >
      <div className={styles.rangeSlider}>
        <Range
          disabled={disabled}
          className={styles.customRcSlider}
          min={rangeValue.min}
          max={rangeValue.max}
          value={rangeValue.value}
          onChange={(value) =>
            handleOnChangeRangeBox(value, props.filterDescriptor.name)
          }
        />
      </div>

      <div className="row no-gutters">
        <div className="col pr-2">
          <div className="input-group">
            <input
              disabled={disabled}
              id="rangeValueFrom"
              type="number"
              value={rangeValue.value[0] ?? 0}
              className="form-control"
              aria-describedby="rangeFrom"
              onChange={(event) =>
                handleOnChangeValueInput(
                  event,
                  rangeValue.value,
                  props.filterDescriptor.name
                )
              }
              onBlur={(event) =>
                handleOnBlurValueInput(
                  event,
                  rangeValue.value,
                  props.filterDescriptor.name
                )
              }
            />
          </div>
        </div>

        <div className="col pl-2">
          <div className="input-group">
            <input
              disabled={disabled}
              id="rangeValueTo"
              type="number"
              value={rangeValue.value[1] ?? 0}
              className="form-control"
              aria-describedby="rangeTo"
              onChange={(event) =>
                handleOnChangeValueInput(
                  event,
                  rangeValue.value,
                  props.filterDescriptor.name
                )
              }
              onBlur={(event) =>
                handleOnBlurValueInput(
                  event,
                  rangeValue.value,
                  props.filterDescriptor.name
                )
              }
            />
          </div>
        </div>
      </div>
    </FormGroup>
  );
};

export default DataFilterRangeBoxCurrency;
